import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { ajaxconfig } from "./ajaxconfig";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import cookie from "universal-cookie";
import QRCode from "react-qr-code";
import cbtAPI from '../api/cbt';
// ❌ REMOVED: import { VerifyPublicGetCbtPassCodeQr } from './serverurl';
import { openInApp, getPlatform } from '../utils/openInApp';
import {
    FaDownload,
    FaMobileAlt,
    FaExternalLinkAlt,
    FaShieldAlt,
    FaFileAlt,
    FaCheckCircle,
    FaIdBadge,
    FaQrcode,
    FaPrint,
    FaTimes,
    FaClock,
    FaUsers,
    FaChartBar,
    FaCopy,
    FaShare,
    FaLink
} from 'react-icons/fa';

const QrsCbtPassCodeVerification = () => {
    const [cbt_data, set_cbt_data] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [showPrintPreview, setShowPrintPreview] = useState(false);
    const [copied, setCopied] = useState(false);
    const cookies = new cookie();
    const [cbt_pass_code_info, set_cbt_pass_code_info] = useState(cookies.get("name"));
    const auth_code = cookies.get("auto_login_code");
    const printRef = useRef();

    const url_param = new URLSearchParams(window.location.search);
    const cbt_passcode = url_param.get("cbt_pass_code");
    const test_id = url_param.get("test_id");
    const [cbt_test_id_verify, set_cbt_test_id_verify] = useState(test_id);
    const navigate = useNavigate();

    // ⭐ Build the deep-link URL ourselves — do NOT rely on the
    // stale VerifyPublicGetCbtPassCodeQr constant (it has the wrong
    // domain: "app.bthost.com" instead of "app.cbthost.com", and
    // the wrong path: "/qr_verification" instead of
    // "/participate/verification").
    //
    // Only include test_id if it's a short, sane value — long or
    // garbage test_ids bloat the QR past the scanner's reliability
    // threshold.
    const sanitizedTestId =
        (test_id &&
            test_id.length <= 40 &&
            /^[A-Za-z0-9_\-]+$/.test(test_id))
            ? test_id
            : null;

    const cbtbarcode =
        `https://app.cbthost.com/participate/verification?cbt_pass_code=${encodeURIComponent(cbt_passcode || '')}` +
        (sanitizedTestId
            ? `&test_id=${encodeURIComponent(sanitizedTestId)}`
            : '');

    // Debug — remove after verifying the fix works
    useEffect(() => {
        console.log('🔲 QR payload:', cbtbarcode);
        console.log('🔲 QR length:', cbtbarcode.length);
        console.log('🔲 test_id raw:', test_id);
        console.log('🔲 test_id sanitized:', sanitizedTestId);
    }, [cbtbarcode, test_id, sanitizedTestId]);

    // ⭐ Deep-link helper — opens app if installed, else falls back to store
    const redirectToApp = (passcode, testId) => {
        openInApp(passcode, testId || undefined);
    };

    const handlePrint = () => {
        setShowPrintPreview(true);
    };

    const handlePrintClose = () => {
        setShowPrintPreview(false);
    };

    const handlePrintPreview = () => {
        setShowPrintPreview(true);
    };

    const handleDownloadPDF = () => {
        window.print();
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            toast.success('Copied to clipboard!');
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const shareTest = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Join ${cbt_data.cbt_name || 'CBT Test'}`,
                    text: `Use this passcode to join the test: ${cbt_passcode}`,
                    url: cbtbarcode,   // ⭐ share the deep-link URL
                });
                toast.success('Test shared successfully!');
            } catch (error) {
                console.log('Error sharing:', error);
            }
        } else {
            copyToClipboard(cbtbarcode);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!cbt_passcode) return;
            setIsLoading(true);
            try {
                const response = await cbtAPI.getPassCodeInfo(cbt_passcode);
                set_cbt_data(response.data || response);
                toast.success('Test information loaded successfully');
            } catch (error) {
                toast.error(error.message || 'Failed to load test information');
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, [cbt_passcode]);

    const getStatusColor = (status) => {
        switch (status) {
            case 'published': return 'bg-green-100 text-green-800 border-green-200';
            case 'draft': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
            case 'archived': return 'bg-gray-100 text-gray-800 border-gray-200';
            default: return 'bg-blue-100 text-blue-800 border-blue-200';
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 py-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-4">
                        <FaShieldAlt className="text-2xl text-white" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
                        Test Verification
                    </h1>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                        Secure access to your computer-based test
                    </p>
                </div>

                {/* ⭐ Mobile-only: Open in App banner */}
                {getPlatform() !== 'desktop' && (
                    <div className="mb-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-3 rounded-2xl shadow-lg flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <FaMobileAlt size={24} />
                            <div>
                                <p className="font-semibold">Open in CBTHost App</p>
                                <p className="text-xs text-green-100">
                                    Faster, safer test experience
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => redirectToApp(cbt_passcode, cbt_test_id_verify)}
                            className="bg-white text-green-700 px-4 py-2 rounded-lg font-semibold text-sm hover:bg-green-50 transition-colors"
                        >
                            Open
                        </button>
                    </div>
                )}

                {/* Main Verification Card */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden transition-all duration-300 hover:shadow-3xl border border-gray-100">
                    <div className="bg-gradient-to-r from-blue-600 via-blue-700 to-indigo-800 px-6 py-6">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                            <div className="flex items-center mb-4 md:mb-0">
                                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center mr-4">
                                    <FaShieldAlt className="text-2xl text-white" />
                                </div>
                                <div>
                                    <h2 className="text-2xl font-bold text-white">
                                        {cbt_data.cbt_name || 'CBT Test'}
                                    </h2>
                                    <p className="text-blue-100 text-sm mt-1">
                                        Secure Test Verification Portal
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <button
                                    onClick={shareTest}
                                    className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-white rounded-xl hover:bg-opacity-30 transition-all duration-200"
                                >
                                    <FaShare className="mr-2" />
                                    Share
                                </button>
                                <button
                                    onClick={handlePrintPreview}
                                    className="inline-flex items-center px-4 py-2 bg-white text-blue-600 rounded-xl hover:bg-blue-50 transition-all duration-200 font-semibold"
                                >
                                    <FaPrint className="mr-2" />
                                    Print
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="p-6 md:p-8">
                        {isLoading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                                <p className="text-gray-600 font-medium">Loading test information...</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                                {/* QR Code Section */}
                                <div className="lg:col-span-1">
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100 text-center">
                                        <div className="flex items-center justify-center text-blue-600 mb-4">
                                            <FaQrcode className="text-2xl mr-3" />
                                            <span className="text-xl font-bold">Quick Access QR</span>
                                        </div>
                                        <div className="bg-white p-4 rounded-xl shadow-inner border border-blue-200 mb-4 flex justify-center">
                                            <QRCode
                                                size={220}
                                                value={cbtbarcode}
                                                level="H"
                                                className="mx-auto"
                                                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                                            />
                                        </div>
                                        <p className="text-sm text-gray-600 bg-white py-2 px-4 rounded-full border border-blue-200">
                                            Scan with mobile device to access test
                                        </p>
                                    </div>
                                </div>

                                {/* Test Details Section */}
                                <div className="lg:col-span-2">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Test Information Card */}
                                        <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                                <FaFileAlt className="text-blue-500 mr-3" />
                                                Test Information
                                            </h3>
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                    <span className="text-gray-600 font-medium">Pass Code</span>
                                                    <div className="flex items-center">
                                                        <span className="font-mono text-lg font-bold text-blue-600 bg-blue-50 px-3 py-1 rounded-lg">
                                                            {cbt_passcode}
                                                        </span>
                                                        <button
                                                            onClick={() => copyToClipboard(cbt_passcode)}
                                                            className="ml-2 text-gray-400 hover:text-blue-500 transition-colors"
                                                        >
                                                            <FaCopy size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                    <span className="text-gray-600 font-medium">Status</span>
                                                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${getStatusColor(cbt_data.cbt_status)}`}>
                                                        {cbt_data.cbt_status || 'N/A'}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center py-2">
                                                    <span className="text-gray-600 font-medium">Access Type</span>
                                                    <span className="text-gray-900 font-semibold">
                                                        {cbt_data.access_type === 'open_access' ? 'Open Access' : 'Restricted'}
                                                    </span>
                                                </div>
                                                {cbt_data.description && (
                                                    <div className="py-2 border-t border-gray-200">
                                                        <span className="text-gray-600 font-medium block mb-1">Description</span>
                                                        <p className="text-gray-900 text-sm">{cbt_data.description}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Quick Actions Card */}
                                        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                                <FaMobileAlt className="text-indigo-500 mr-3" />
                                                Quick Actions
                                            </h3>
                                            <div className="space-y-3">
                                                <button
                                                    onClick={() => redirectToApp(cbt_passcode, cbt_test_id_verify)}
                                                    className="w-full flex items-center justify-center px-4 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg"
                                                >
                                                    <FaExternalLinkAlt className="mr-3" />
                                                    <span className="font-semibold">Start Test Now</span>
                                                </button>

                                                <button
                                                    onClick={() => copyToClipboard(cbtbarcode)}
                                                    className="w-full flex items-center justify-center px-4 py-3 bg-white text-gray-700 border border-gray-300 rounded-xl hover:bg-gray-50 transition-all duration-200"
                                                >
                                                    <FaLink className="mr-3" />
                                                    <span>Copy Test Link</span>
                                                </button>
                                            </div>
                                        </div>

                                        {/* Additional Details */}
                                        <div className="md:col-span-2 bg-white rounded-2xl p-6 border border-gray-200">
                                            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                                                <FaChartBar className="text-green-500 mr-3" />
                                                Test Specifications
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                                <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-100">
                                                    <FaClock className="text-blue-500 text-2xl mx-auto mb-2" />
                                                    <p className="text-sm text-gray-600">Duration</p>
                                                    <p className="font-bold text-gray-900">
                                                        {cbt_data.cbt_duration ? `${cbt_data.cbt_duration} mins` : 'Flexible'}
                                                    </p>
                                                </div>
                                                <div className="text-center p-4 bg-green-50 rounded-xl border border-green-100">
                                                    <FaUsers className="text-green-500 text-2xl mx-auto mb-2" />
                                                    <p className="text-sm text-gray-600">Attempts Allowed</p>
                                                    <p className="font-bold text-gray-900">
                                                        {cbt_data.attempt_limit || 'Unlimited'}
                                                    </p>
                                                </div>
                                                <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-100">
                                                    <FaCheckCircle className="text-purple-500 text-2xl mx-auto mb-2" />
                                                    <p className="text-sm text-gray-600">Default Score</p>
                                                    <p className="font-bold text-gray-900">
                                                        {cbt_data.default_score ? `${cbt_data.default_score} pt` : '1 pt'}
                                                    </p>
                                                </div>
                                            </div>
                                            {(cbt_data.passing_score || cbt_data.negative_marking) && (
                                                <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                                                    {cbt_data.passing_score && (
                                                        <div className="flex justify-between border-t border-gray-200 pt-3">
                                                            <span className="text-gray-600">Passing Score:</span>
                                                            <span className="font-semibold">{cbt_data.passing_score}%</span>
                                                        </div>
                                                    )}
                                                    {cbt_data.negative_marking && (
                                                        <div className="flex justify-between border-t border-gray-200 pt-3">
                                                            <span className="text-gray-600">Negative Marking:</span>
                                                            <span className="font-semibold">{cbt_data.negative_mark_percentage}%</span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Print Preview Modal */}
            {showPrintPreview && (
                <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[95vh] flex flex-col animate-scale-in">
                        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white z-10 rounded-t-2xl">
                            <div className="flex items-center">
                                <FaPrint className="text-blue-600 text-xl mr-3" />
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">Print Preview</h3>
                                    <p className="text-sm text-gray-600">Review before printing</p>
                                </div>
                            </div>
                            <button
                                onClick={handlePrintClose}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1">
                            <div
                                className="bg-white p-8 border-2 border-dashed border-gray-300 rounded-lg print-container"
                                id="print-content"
                            >
                                <div className="text-center mb-8 border-b border-gray-200 pb-6">
                                    <div className="flex items-center justify-center mb-4">
                                        <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                            <FaShieldAlt className="text-2xl text-white" />
                                        </div>
                                        <div>
                                            <h1 className="text-3xl font-bold text-gray-800">CBT Verification Document</h1>
                                            <p className="text-gray-600 mt-1">Official Test Access Credentials</p>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-500">
                                        Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                                    <div className="text-center">
                                        <div className="bg-gray-50 p-6 rounded-xl border border-gray-200 inline-block">
                                            <div className="flex items-center justify-center text-gray-700 mb-4">
                                                <FaQrcode className="text-xl mr-2" />
                                                <span className="font-bold text-lg">Verification QR Code</span>
                                            </div>
                                            <QRCode
                                                size={220}
                                                value={cbtbarcode}
                                                level="H"
                                                className="mx-auto"
                                            />
                                            <p className="text-gray-600 mt-3 text-sm">
                                                Scan to access test instantly
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-4">
                                        <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                                            <h3 className="font-bold text-blue-800 text-lg mb-2">Primary Access Code</h3>
                                            <p className="font-mono text-2xl text-blue-600 font-bold">{cbt_passcode}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <h4 className="font-semibold text-gray-700 text-sm mb-1">Test Name</h4>
                                                <p className="text-gray-900 font-medium">{cbt_data.cbt_name || 'N/A'}</p>
                                            </div>
                                            <div className="bg-gray-50 p-3 rounded-lg">
                                                <h4 className="font-semibold text-gray-700 text-sm mb-1">Status</h4>
                                                <p className={`font-medium ${cbt_data.cbt_status === 'published' ? 'text-green-600' : 'text-yellow-600'}`}>
                                                    {cbt_data.cbt_status || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h3 className="text-xl font-bold text-gray-800 border-b border-gray-300 pb-2 mb-4 flex items-center">
                                        <FaFileAlt className="mr-3 text-blue-500" />
                                        Test Specifications
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-1">Access Type</h4>
                                            <p className="text-gray-900">{cbt_data.access_type === 'open_access' ? 'Open Access' : 'Restricted Access'}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-1">Duration</h4>
                                            <p className="text-gray-900">{cbt_data.cbt_duration ? `${cbt_data.cbt_duration} minutes` : 'Not specified'}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-1">Max Attempts</h4>
                                            <p className="text-gray-900">{cbt_data.attempt_limit || 'Unlimited'}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-1">Default Score</h4>
                                            <p className="text-gray-900">{cbt_data.default_score || 1} point(s)</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-1">Passing Score</h4>
                                            <p className="text-gray-900">{cbt_data.passing_score ? `${cbt_data.passing_score}%` : 'Not set'}</p>
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-gray-700 mb-1">Negative Marking</h4>
                                            <p className="text-gray-900">{cbt_data.negative_marking ? `${cbt_data.negative_mark_percentage}%` : 'No'}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className="mb-6">
                                    <h3 className="text-xl font-bold text-gray-800 border-b border-gray-300 pb-2 mb-4">Test Rules & Settings</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                                        <div className="flex items-center">
                                            <span className="w-4 h-4 mr-2 text-green-600">✓</span> Randomize Questions: {cbt_data.randomize_questions ? 'Yes' : 'No'}
                                        </div>
                                        <div className="flex items-center">
                                            <span className="w-4 h-4 mr-2 text-green-600">✓</span> Randomize Options: {cbt_data.randomize_options ? 'Yes' : 'No'}
                                        </div>
                                        <div className="flex items-center">
                                            <span className="w-4 h-4 mr-2 text-green-600">✓</span> Back Navigation: {cbt_data.allow_back_navigation ? 'Allowed' : 'Not allowed'}
                                        </div>
                                        <div className="flex items-center">
                                            <span className="w-4 h-4 mr-2 text-green-600">✓</span> Question Review: {cbt_data.allow_question_review ? 'Yes' : 'No'}
                                        </div>
                                        <div className="flex items-center">
                                            <span className="w-4 h-4 mr-2 text-green-600">✓</span> Show Score Immediately: {cbt_data.show_score_immediately ? 'Yes' : 'No'}
                                        </div>
                                        <div className="flex items-center">
                                            <span className="w-4 h-4 mr-2 text-green-600">✓</span> Webcam Required: {cbt_data.require_webcam ? 'Yes' : 'No'}
                                        </div>
                                    </div>
                                </div>

                                <div className="text-center text-gray-500 text-xs border-t border-gray-200 pt-6">
                                    <p className="mb-2">This is an official verification document.</p>
                                    <p>© {new Date().getFullYear()} CBTHost System • {window.location.hostname}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center gap-4 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
                            <button
                                onClick={handlePrintClose}
                                className="px-6 py-3 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 transition-colors flex items-center font-medium"
                            >
                                <FaTimes className="mr-2" />
                                Cancel
                            </button>
                            <button
                                onClick={handleDownloadPDF}
                                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center font-semibold shadow-lg"
                            >
                                <FaPrint className="mr-2" />
                                Print Document
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer
                position="top-center"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="colored"
                toastClassName="rounded-xl"
            />

            <style>
                {`
                    @media print {
                        body * { visibility: hidden; }
                        #print-content, #print-content * { visibility: visible; }
                        #print-content {
                            position: absolute;
                            top: 0;
                            left: 0;
                            width: 210mm;
                            min-height: 297mm;
                            padding: 20mm;
                            margin: 0;
                            box-sizing: border-box;
                            background: white;
                            font-size: 12pt;
                        }
                        .print-container { width: 100%; height: 100%; }
                        @page { margin: 0; size: A4; }
                    }
                    @keyframes scale-in {
                        0% { opacity: 0; transform: scale(0.9); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    .animate-scale-in { animation: scale-in 0.3s ease-out; }
                `}
            </style>
        </div>
    );
};

export { QrsCbtPassCodeVerification };